// Mock API service for development when backend is not available
import {
  User,
  UserProfile,
  UserPreferences,
  UserStats,
} from '@coffeeplexity/shared';

// Mock data
const mockUser: User = {
  id: '1',
  email: 'john.doe@example.com',
  username: 'johndoe',
  first_name: 'John',
  last_name: 'Doe',
  display_name: 'John Doe',
  avatar_url:
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
  bio: 'Coffee enthusiast and home barista',
  role: 'user' as any,
  status: 'active' as any,
  created_at: '2023-01-01T00:00:00Z',
  updated_at: '2023-01-01T00:00:00Z',
};

const mockProfile: UserProfile = {
  user_id: '1',
  bio: 'Passionate about specialty coffee and brewing techniques. Always exploring new flavors and methods.',
  location: 'San Francisco, CA',
  website: 'https://johndoe.coffee',
  social_links: {
    instagram: '@johndoe_coffee',
    twitter: '@johndoe',
    youtube: 'JohnDoeBrews',
    linkedin: 'john-doe-coffee',
    github: 'johndoe',
    website: 'https://johndoe.coffee',
    email: 'john@johndoe.coffee',
  },
  preferences: {
    email_notifications: true,
    push_notifications: true,
    privacy_level: 'public',
    preferred_units: 'metric',
    theme: 'auto',
  },
  stats: {
    recipes_created: 12,
    recipes_liked: 24,
    followers_count: 156,
    following_count: 89,
    total_views: 1247,
  },
  // AI Journey fields
  ai_level: 8,
  ai_experience: 750,
  ai_experience_to_next_level: 1000,
  verified: true,
  achievements: [
    'First Recipe Created',
    'Social Butterfly',
    'Coffee Enthusiast',
    'Recipe Master',
    'Consistent Brewer',
  ],
  current_streak: 15,
  total_brews: 89,
  average_rating: 4.2,
  mastery_areas: ['Espresso', 'Pour Over', 'French Press'],
  // Enhanced profile fields
  total_comments: 45,
  total_shares: 23,
  profile_completion_percentage: 85,
  last_active: '2024-01-15T10:30:00Z',
  join_date: '2023-01-01T00:00:00Z',
};

const mockStats: UserStats = {
  recipes_created: 12,
  recipes_liked: 24,
  followers_count: 156,
  following_count: 89,
  total_views: 1247,
  total_comments: 45,
  total_shares: 23,
  total_likes_received: 234,
  engagement_rate: 4.8,
  average_likes_per_recipe: 19.5,
  completion_rate: 24, // 12/50 recipes
  // AI Journey stats
  ai_level: 8,
  ai_experience: 750,
  current_streak: 15,
  total_brews: 89,
  average_rating: 4.2,
  achievements_count: 5,
  mastery_areas_count: 3,
};

const mockPreferences: UserPreferences = {
  email_notifications: true,
  push_notifications: true,
  privacy_level: 'public',
  preferred_units: 'metric',
  theme: 'auto',
};

const mockNotificationPreferences = {
  email_notifications: true,
  push_notifications: true,
  recipe_likes: true,
  recipe_comments: true,
  new_followers: true,
  creator_updates: true,
  system_announcements: true,
  marketing_emails: false,
};

// Simulate network delay
const delay = (ms: number = 500) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const mockApi = {
  // User API mocks
  user: {
    getProfile: async (): Promise<UserProfile> => {
      await delay();
      return mockProfile;
    },

    updateProfile: async (data: any): Promise<User> => {
      await delay();
      return { ...mockUser, ...data };
    },

    getPreferences: async (): Promise<UserPreferences> => {
      await delay();
      return mockPreferences;
    },

    updatePreferences: async (data: any): Promise<UserPreferences> => {
      await delay();
      return { ...mockPreferences, ...data };
    },

    getStats: async (): Promise<UserStats> => {
      await delay();
      return mockStats;
    },

    uploadAvatar: async (imageUri: string) => {
      await delay(1000); // Longer delay for upload
      // In a real scenario, we'd upload to a server and get back a URL
      // For mock, we'll use the provided imageUri or a placeholder
      const avatarUrl = imageUri.startsWith('http')
        ? imageUri
        : 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';

      return {
        avatar_url: avatarUrl,
        message: 'Avatar updated successfully',
      };
    },
  },

  // Notification API mocks
  notifications: {
    getPreferences: async () => {
      await delay();
      return mockNotificationPreferences;
    },

    updatePreferences: async (data: any) => {
      await delay();
      return { ...mockNotificationPreferences, ...data };
    },

    getUnreadCount: async () => {
      await delay();
      return { count: Math.floor(Math.random() * 10) };
    },
  },
};
